import type { ChangeEvent, FormEvent } from 'react'
import { useEffect, useState, useCallback, useRef } from 'react'
import { navigate } from 'gatsby'
import classNames from 'classnames'
import { LopOutline } from 'src/components/Icons/LopOutline'
import { checkEnviromentIsB2B } from 'src/utils/checkEnviroment'
import { B2B } from 'src/store-config/B2B-Config'
import { B2C } from 'src/store-config/B2C-Config'
import { useDebounce } from 'src/hooks/useDebounce'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import DOMPurify from 'dompurify'
import { sendAutoCompleteEvent } from 'src/sdk/analytics/platform/intelligentSearch'
import type { SearchAutocomplete } from 'src/sdk/analytics/types'
import { getDeviceInfo } from 'src/utils/dynamicYield/getUserDeviceObject'
import { ensureDYCookies } from 'src/utils/dynamicYield/dynamicYieldCookies'
import { useLocation } from 'react-use'
import { useMobile } from 'src/hooks/useMobile'
import { SearchHintIcon } from 'src/components/Icons/SearchHintIcon'
import type { Product } from 'src/components/restructure/product/types/product'
import CloseIcon from 'src/images/icons/CloseIcon'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { clientStore } from '../../../services/axiosInstance'
import { ModalSearch } from './ModalSearch'
import * as storeConfig from '../../../../store.config'
import type { ParamsApi, Suggestion } from './types'
import { InputMaskSearch } from './InputMaskSearch'
import { saveSearchedTerms } from './utils/updateUserSearchedTerms'

const { decathlonSearch } = storeConfig

const suggestionsCache = new Map<string, any>()

async function fetchAutoCompleteSuggestions(term: string) {
  if (suggestionsCache.has(term)) {
    return suggestionsCache.get(term)
  }

  if (term.length < 3) {
    return undefined
  }

  const response = await clientStore.get(
    `${decathlonSearch}/autocomplete_suggestions`,
    {
      params: { term, engine: 'vtex' },
    }
  )

  suggestionsCache.set(term, response.data)

  return response.data
}

async function fetchTopSearchedTerms() {
  const response = await clientStore.get(
    '/_v/api/decathlon-search/top_searches?engine=vtex'
  )

  return response.data
}

type ResponseDyDataRecomendation = {
  position: 'top' | 'middle'
  showcases: [
    {
      displays: [
        {
          recommendations: any[]
        }
      ]
    }
  ]
}

async function fetchRecommendations(
  isMobile: boolean,
  href: string,
  setDyDataRecommendations: (data: ResponseDyDataRecomendation) => void
) {
  try {
    const device = await getDeviceInfo()
    const { dyid, sessionId } = await ensureDYCookies()
    const params = {
      dyuser: dyid,
      dysession: sessionId,
      pageName: 'search',
      location: href,
      source: isMobile ? 'mobile' : 'desktop',
    }

    const { data } = await clientStore.get(
      '/_v3/api/decathlon-search/pages/recommendations',
      {
        params: {
          ...params,
          engine: 'dy',
          device,
          name: 'autocomplete',
        },
      }
    )

    setDyDataRecommendations(data[0])
  } catch (err) {
    console.error('Erro no DY:', err)
  }
}

interface SearchInputProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  thinStyle?: boolean
}

export function SearchInput({
  isOpen,
  thinStyle = false,
  setIsOpen,
}: SearchInputProps) {
  const { isMobile } = useMobile()
  const [dyDataRecommendations, setDyDataRecommendations] = useState<any>([])
  const [products, setProducts] = useState<Product[]>([])
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [term, setTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')
  const [termNotFoundResults, setTermNotFoundResults] = useState(false)
  const [userSearchedTerms, setUserSearchedTerms] = useState<
    Array<{ term: string }>
  >([])

  const { pageData } = useGlobalContext()

  const modalSearchRef = useRef<HTMLDivElement>(null)

  const hasRecommendationFetched = useRef(false)

  const standardObj = getStandardObject(pageData)
  const isB2B = checkEnviromentIsB2B()
  const { href } = useLocation()

  const handleTopSearchedTerms = useCallback(async () => {
    const topSearches = await fetchTopSearchedTerms()

    if (!term.length) {
      setSuggestions(topSearches.searches)
      setProducts(topSearches?.products ?? [])
      setTermNotFoundResults(false)

      return
    }

    const dataSuggestions = await fetchAutoCompleteSuggestions(term)

    if (dataSuggestions?.searchSuggestions?.searches.length > 0) {
      setSuggestions(dataSuggestions?.searchSuggestions?.searches)

      return
    }

    setSuggestions([{ term }])
  }, [term])

  const handleIntelligentSerachAutoCompleteEvent = ({
    misspelled,
    operator,
    text,
    results,
  }: Omit<SearchAutocomplete['params'], 'url'>) => {
    sendAutoCompleteEvent({
      name: 'search_autocomplete',
      params: {
        misspelled,
        operator,
        text,
        results,
        url: window.location.href,
      },
    })
  }

  const handleSearchPerformedEvent = useDebounce((value: string) => {
    dispatchAmplitudeEvent({
      eventName: 'Search Performed',
      eventData: {
        ...standardObj,
        'search term': value,
      },
    })
  }, 500)

  const updateDebouncedTerm = useDebounce((value: string) => {
    setDebouncedTerm(value)
  }, 300)

  function handleInputClick() {
    dispatchAmplitudeEvent({
      eventName: 'Click Input',
      eventData: {
        ...standardObj,
        'click name': 'Busca',
      },
    })

    setIsOpen(true)
    handleTopSearchedTerms()
  }

  function handleSearch(searchTerm: string, e?: FormEvent<HTMLFormElement>) {
    if (searchTerm.length < 3) {
      return
    }

    e?.currentTarget?.querySelector('input')?.blur()

    setIsOpen(false)
    setTerm(searchTerm)
    saveSearchedTerms(searchTerm)
    setUserSearchedTerms((prev) => {
      if (prev.some((prevTerm) => prevTerm.term === searchTerm.trim())) {
        return prev
      }

      return [...prev, { term: searchTerm }]
    })
    navigate(
      `/pesquisa?q=${encodeURIComponent(searchTerm)}&sort=score_desc&page=0`
    )
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const cleanValue = DOMPurify.sanitize(e.target.value)

    setTerm(cleanValue)
    updateDebouncedTerm(cleanValue)

    if (cleanValue.length < 3) {
      return
    }

    handleSearchPerformedEvent(cleanValue)
  }

  function handleOnCloseModal() {
    setIsOpen(false)
  }

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const dataSuggestions = await fetchAutoCompleteSuggestions(
          debouncedTerm
        )

        if (!dataSuggestions) {
          if (debouncedTerm === '') {
            handleTopSearchedTerms()
          }

          return
        }

        if (!dataSuggestions.productSuggestions.products.length) {
          const params: ParamsApi = {
            term: debouncedTerm,
            page: 1,
            resultsPerPage: 5,
            sc: isB2B ? B2B.salesChannel : B2C.salesChannel,
            sort: 'relevance',
            engine: 'vtex',
          }

          const searchParams = new URLSearchParams(params as any).toString()

          const { data: searchProducts } = await clientStore.get(
            `/_v3/api/decathlon-search/product/search?${searchParams.toString()}`
          )

          setProducts(searchProducts)
          setSuggestions([{ term: debouncedTerm }])
          setTermNotFoundResults(false)

          return
        }

        handleIntelligentSerachAutoCompleteEvent({
          text: dataSuggestions.searchTerm,
          misspelled: dataSuggestions.misspelled,
          operator: dataSuggestions.operator,
          results: dataSuggestions.count,
        })

        setSuggestions(dataSuggestions?.searchSuggestions?.searches)
        setProducts(dataSuggestions?.productSuggestions?.products)
        setTermNotFoundResults(false)
      } catch (error) {
        setProducts([])
        setSuggestions([])
        console.error(error)
      }
    }

    fetchProducts()
  }, [handleTopSearchedTerms, isB2B, debouncedTerm])

  useEffect(() => {
    if (!hasRecommendationFetched.current) {
      fetchRecommendations(!!isMobile, href ?? '', setDyDataRecommendations)
      hasRecommendationFetched.current = true
    }
  }, [dyDataRecommendations, href, isMobile])

  useEffect(() => {
    const storedTerms = localStorage.getItem('searchedTerms')

    if (storedTerms) {
      setUserSearchedTerms(JSON.parse(storedTerms))
    }
  }, [])

  const dynamicProductData =
    dyDataRecommendations?.showcases?.[0]?.displays?.[0]?.recommendations?.slice(
      0,
      6
    ) ?? []

  return (
    <div
      className={classNames('restructure-small-desktop:!relative', {
        'fixed z-50 top-0 left-0 restructure-small-desktop:top-auto restructure-small-desktop:left-auto w-full h-full restructure-small-desktop:w-auto restructure-small-desktop:h-auto pt-4 restructure-small-desktop:pt-0 bg-restructure-background-primary restructure-small-desktop:bg-transparent flex flex-col':
          isOpen,
      })}
    >
      <div
        ref={modalSearchRef}
        className={classNames({
          'h-screen overflow-y-auto overflow-x-hidden z-10 scrollbar-v2':
            isMobile && isOpen,
        })}
      >
        <div
          className={classNames(
            'px-md flex-col items-center gap-xs restructure-small-desktop:px-0 restructure-small-desktop:relative',
            { '!px-0': !isOpen }
          )}
        >
          <div
            className={classNames({
              'flex w-full items-center gap-xs': isOpen && isMobile,
            })}
          >
            <form
              className={`border-2 border-transparent focus-within:border-[#3643BA] focus-within:bg-white flex-1 z-[9999] bg-restructure-background-secondary rounded-round flex items-center gap-xs px-sm py-[17.5px] sm:h-full ${
                thinStyle && !isOpen ? 'h-10' : 'h-14'
              }`}
              onSubmit={(e) => {
                e.preventDefault()
                handleSearch(term, e)
              }}
            >
              <button
                type="submit"
                aria-label="Buscar"
                data-test-id="botao-busca"
              >
                <LopOutline
                  width={thinStyle && !isOpen ? '16' : '24'}
                  height={thinStyle && !isOpen ? '16' : '24'}
                />
              </button>
              <div className="relative w-full">
                <InputMaskSearch
                  onClick={() => handleInputClick()}
                  onChange={(e) => handleChange(e)}
                  value={term}
                  thinStyle={thinStyle && !isOpen}
                />
              </div>
              {isOpen && !isMobile && (
                <button
                  className="desktop-body-semibold-text3 text-restructure-action"
                  type="button"
                  onClick={() => {
                    setTerm('')
                    setTermNotFoundResults(false)
                    isOpen && handleTopSearchedTerms()
                    setIsOpen(false)
                    dispatchAmplitudeEvent({
                      eventName: 'Click Icons',
                      eventData: {
                        ...standardObj,
                        'click name': 'Cancelar Busca',
                      },
                    })
                  }}
                >
                  Cancelar
                </button>
              )}
            </form>
            {isOpen && isMobile && (
              <button
                className="shrink-0"
                type="button"
                onClick={() => handleOnCloseModal()}
                aria-label="Close modal search"
              >
                <CloseIcon />
              </button>
            )}
          </div>
          {isOpen && (
            <div
              className={classNames(
                'items-center justify-start mt-1 w-full restructure-small-desktop:absolute',
                {
                  'hidden md:flex': term === '',
                  flex: term !== '',
                }
              )}
            >
              <SearchHintIcon />
              <span className="text-xs pt-1">
                <b>Dica:</b> Buscas detalhadas funcionam melhor
              </span>
            </div>
          )}
        </div>
        <ModalSearch
          isOpen={isOpen}
          onClose={() => handleOnCloseModal()}
          onSearch={(searchTerm) => handleSearch(searchTerm ?? term)}
          products={products}
          dynamicProductsSuggestions={dynamicProductData}
          dynamicRecommendations={dynamicProductData}
          suggestions={suggestions ?? handleTopSearchedTerms()}
          term={term}
          resultNotFound={termNotFoundResults}
          setUserSearchedTerms={setUserSearchedTerms}
          userSearchedTerms={userSearchedTerms}
          modalSearchEl={modalSearchRef?.current}
        />
      </div>
    </div>
  )
}
